import { Injectable } from "@angular/core";
import { AbstractControl, ValidationErrors } from "@angular/forms";
import { GeoService } from "../../viewer-components/services/internal-services/geo.service";

@Injectable({
  providedIn: "root",
})
export class FormValidatorService {
  /**
   * Known ArcGIS service types (last path segment).
   * Used to detect the service family from an ArcGIS REST URL.
   */
  static readonly ARCGIS_SERVICE_TYPES: string[] = [
    "FeatureServer",
    "GeocodeServer",
    "GeoDataServer",
    "GeometryServer",
    "GPServer",
    "GlobeServer",
    "ImageServer",
    "MapServer",
    "MobileServer",
    "SceneServer",
    "SchematicsServer",
    "StreamServer",
    "TopographicProductionServer",
    "NetworkDiagramServer",
    "ValidationServer",
    "VectorTileServer",
    "VersionManagementServer",
  ];

  /** Canonical ArcGIS service type names. */
  static readonly ARCGIS_FEATURE_SERVER = "FeatureServer";

  static readonly ARCGIS_MAP_SERVER = "MapServer";

  /**
   * Validates a generic web URL using a permissive regex.
   * Does not handle required; combine with Validators.required.
   * @param control AbstractControl
   * @returns ValidationErrors | null
   */
  static genericUrl(control: AbstractControl): ValidationErrors | null {
    const url = (control.value || "").trim();
    if (!url) return null; // required is handled by Validators.required
    const pattern = new RegExp(
      `(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})`,
      "i",
    );
    return pattern.test(url) ? null : { url: true };
  }

  /**
   * Validates that the URL points to an ArcGIS FeatureServer endpoint and returns extra details for UI messaging.
   * It includes the detected service type (if any) and a human-friendly message under 'arcgisMessage' when invalid.
   * Does not handle required; combine with Validators.required.
   * @param control AbstractControl
   * @returns ValidationErrors | null
   */
  static arcgisFeatureServiceUrlDetailed(control: AbstractControl): ValidationErrors | null {
    const url = (control.value || "").trim();
    if (!url) return null; // required is handled by Validators.required
    const type = FormValidatorService.getArcgisServiceType(url);
    if (type === FormValidatorService.ARCGIS_FEATURE_SERVER) return null;

    let message: string;
    if (type === FormValidatorService.ARCGIS_MAP_SERVER) {
      message = "The URL you entered is a Map Service URL. Please enter a Feature Service URL.";
    } else if (type) {
      message = `The URL you entered is an ${type} URL. Please enter a Feature Service URL.`;
    } else {
      message = "The URL you entered is not an ArcGIS Feature Service URL. Please enter a Feature Service URL.";
    }
    return { arcgis: true, arcgisType: type, arcgisMessage: message };
  }

  /**
   * Extracts the ArcGIS service type from a REST URL by reading the last path segment.
   * If the last segment matches a known service type, that value is returned; otherwise null.
   * @param url string
   * @returns string | null
   */
  static getArcgisServiceType(url: string): string | null {
    if (!url) return null;
    const parts = url.split("?")[0].split("/").filter(Boolean);
    const last = parts[parts.length - 1];
    return FormValidatorService.ARCGIS_SERVICE_TYPES.includes(last) ? last : null;
  }

  /**
   * Validates WKT text content by parsing to GeoJSON and running GeoJSON validation.
   * Does not handle required; combine with Validators.required.
   * @param control AbstractControl
   * @returns ValidationErrors | null
   */
  static wkt(control: AbstractControl): ValidationErrors | null {
    const value = (control.value || "").trim();
    if (!value) return null; // required is handled by Validators.required
    try {
      const featureCollection = GeoService.wktToFeatureCollection(value, false);
      const errors = GeoService.validateGeoJSON(featureCollection);
      return errors.length === 0 ? null : { invalidText: true };
    } catch {
      return { invalidText: true };
    }
  }

  /**
   * Validates GeoJSON text content by parsing JSON and running GeoJSON validation.
   * Does not handle required; combine with Validators.required.
   * @param control AbstractControl
   * @returns ValidationErrors | null
   */
  static geojson(control: AbstractControl): ValidationErrors | null {
    const value = (control.value || "").trim();
    if (!value) return null; // required is handled by Validators.required
    try {
      const parsed = JSON.parse(value);
      const errors = GeoService.validateGeoJSON(parsed);
      return errors.length === 0 ? null : { invalidText: true };
    } catch {
      return { invalidText: true };
    }
  }
}
